import { useQuery } from '@tanstack/react-query';
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Icon from 'shopper/components/Icon';

import useCouponPicker from 'hooks/useCouponPicker';

import { getStoreImageUrl } from 'lib/image';
import trackEvent from 'lib/posthog/trackEvent';
import { couponQueryKeys } from 'lib/queryKeys';

import { getCouponsList } from 'services/coupon';

import LINKS from 'constants/links';

const GA_EVENT_CATEGORY = 'home_sidebar_coupons';

const MOCK_COUPONS = [
  {
    couponId: null,
    storeDomain: 'amazon.com.br',
    storeName: 'Amazon',
    storeSlug: 'amazon',
    couponDiscountShort: 'Cupons Amazon',
  },
  {
    couponId: null,
    storeDomain: 'mercadolivre.com.br',
    storeName: 'Mercado Livre',
    storeSlug: 'mercado-livre',
    couponDiscountShort: 'Cupons Mercado Livre',
  },
  {
    couponId: null,
    storeDomain: 'shopee.com.br',
    storeName: 'Shopee',
    storeSlug: 'shopee',
    couponDiscountShort: 'Cupons Shopee',
  },
  {
    couponId: null,
    storeDomain: 'aliexpress.com',
    storeName: 'AliExpress',
    storeSlug: 'aliexpress',
    couponDiscountShort: 'Cupons AliExpress',
  },
];

const CouponsCard = ({ initialCoupons = [] }) => {
  const { openCouponPicker } = useCouponPicker(GA_EVENT_CATEGORY);

  const { data: coupons = MOCK_COUPONS } = useQuery({
    queryKey: couponQueryKeys.list({ sortType: null, sidebar: true }),
    queryFn: ({ signal }) => getCouponsList({ limit: 4 }, { signal }),
    initialData:
      initialCoupons.length > 0 ? initialCoupons.slice(0, 4) : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: (data) =>
      Array.isArray(data) && data.length > 0 ? data.slice(0, 4) : MOCK_COUPONS,
  });

  return (
    <div>
      <NextLink href={LINKS.COUPONS} passHref>
        <a className="flex w-full cursor-pointer items-center justify-between rounded-4 bg-neutral-high-100 px-4 py-3 transition-colors hover:bg-neutral-high-200 dark:bg-neutral-low-400 dark:hover:bg-neutral-low-300">
          <div className="flex items-center gap-2">
            <Icon
              className="text-neutral-low-400 dark:text-neutral-high-200"
              name="coupon"
              size="size3"
            />
            <span className="text-sm font-medium text-neutral-low-400 dark:text-neutral-high-200">
              Cupons de descontos
            </span>
          </div>
          <Icon
            className="text-primary-300"
            name="chevron-right"
            size="size3"
          />
        </a>
      </NextLink>

      <div className="mt-3 flex flex-col gap-3">
        {coupons.map((coupon, index) => {
          const cardClassName =
            'relative flex w-full cursor-pointer items-stretch rounded-3 bg-neutral-high-100 text-left transition-colors hover:bg-neutral-high-200 dark:bg-neutral-low-400 dark:hover:bg-neutral-low-300';

          const cardInner = (
            <>
              {/* Esquerda — logo da loja */}
              <div className="flex w-[72px] shrink-0 items-center justify-center p-3">
                {coupon.storeImage ? (
                  <img
                    alt={coupon.storeName}
                    className="max-h-[32px] w-auto object-contain"
                    src={getStoreImageUrl(coupon.storeImage, 60)}
                  />
                ) : (
                  <span className="text-xs font-bold text-neutral-low-400 dark:text-neutral-high-300">
                    {(coupon.storeDomain || '').split('.')[0].toUpperCase()}
                  </span>
                )}
              </div>

              {/* Divisor tracejado */}
              <div className="self-stretch border-r border-dashed border-neutral-high-300 dark:border-neutral-low-200" />

              {/* Direita — info */}
              <div className="flex flex-1 flex-col gap-0.5 p-3">
                <span className="flex items-center gap-1 text-xs text-neutral-low-300 dark:text-neutral-high-300">
                  {coupon.storeDomain}
                  <Icon
                    className="text-neutral-high-400"
                    name="verified"
                    size="size3"
                  />
                </span>
                <span className="text-base font-bold text-neutral-low-400 dark:text-neutral-high-200">
                  {coupon.couponDiscountShort}
                </span>
              </div>
            </>
          );

          if (!coupon.couponId) {
            return (
              <NextLink
                key={index}
                href={`/cupons/loja/${coupon.storeSlug}/`}
                passHref
              >
                <a
                  className={cardClassName}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {cardInner}
                </a>
              </NextLink>
            );
          }

          return (
            <button
              key={coupon.couponId}
              className={cardClassName}
              type="button"
              onClick={() => {
                openCouponPicker({
                  couponId: coupon.couponId,
                  storeName: coupon.storeName,
                  storeSlug: coupon.storeSlug,
                });
                trackEvent(
                  'click home sidebar coupon',
                  {
                    coupon_id: coupon.couponId,
                    store_slug: coupon.storeSlug,
                  },
                  { destination: 'posthog' }
                );
              }}
            >
              {cardInner}
            </button>
          );
        })}
      </div>
    </div>
  );
};

CouponsCard.propTypes = {
  initialCoupons: PropTypes.array,
};

export default CouponsCard;
